import { useState } from 'react';

import { useNavigate } from '@tanstack/react-router';

import { Bell, TrendingUp, TriangleAlert, UserCheck } from 'lucide-react';

import {
  Button,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@src/components/ui';
import { useNotificationMarkReadMutation, useNotificationsQuery } from '@src/query';


const POLL_MS = 30 * 1000;

/** Per-event presentation for the feed (icon + colour box), matching the .pen
 * Notif Panel. Unknown events fall back to the neutral bell treatment. */
const EVENT_META = {
  risk_created: { icon: TriangleAlert, box: 'bg-danger/12 text-danger' },
  invite_accepted: { icon: UserCheck, box: 'bg-success/12 text-success' },
  weekly_summary: { icon: TrendingUp, box: 'bg-surface-2 text-ink-subtle' },
};


const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;


/** Compact relative label ("now", "5m", "3h", "2d", or a date). Computed against
 * a `now` captured at fetch time (in the query `select`) so render stays pure. */
function relativeTime(createdAt, now) {
  const then = new Date(createdAt).getTime();
  if (Number.isNaN(then)) {
    return '';
  }
  const diff = Math.max(0, now - then);
  if (diff < MINUTE) {
    return 'now';
  }
  if (diff < HOUR) {
    return `${Math.floor(diff / MINUTE)}m ago`;
  }
  if (diff < DAY) {
    return `${Math.floor(diff / HOUR)}h ago`;
  }
  if (diff < 7 * DAY) {
    return `${Math.floor(diff / DAY)}d ago`;
  }
  return new Date(createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}


/** Append the relative-time label to each row at fetch time so the component
 * never reads the clock during render. Re-runs on every poll/refetch. */
function selectFeed(envelope) {
  const now = Date.now();
  const results = (envelope?.results || []).map((item) => ({
    ...item,
    timeLabel: relativeTime(item.created_at, now),
  }));
  return { ...envelope, results, unread: envelope?.unread || 0 };
}


function NotificationRow({ notification, onClick }) {
  const meta = EVENT_META[notification.event] || { icon: Bell, box: 'bg-surface-2 text-ink-subtle' };
  const Icon = meta.icon;
  const unread = !notification.read_at;

  return (
    <Button
      variant="ghost"
      className={cn(
        'h-auto w-full items-start justify-start gap-3 rounded-none border-b border-hairline',
        'px-4 py-[13px] text-left font-normal text-ink last:border-b-0 hover:bg-surface-2 hover:text-ink',
      )}
      onClick={onClick}
    >
      <span className={cn('grid size-[30px] shrink-0 place-items-center rounded-lg', meta.box)}>
        <Icon className="size-[15px]!" />
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <span className="text-[13px] font-medium leading-[1.35] text-ink">{notification.title}</span>
        <span className="text-[11px] text-ink-subtle">{notification.timeLabel}</span>
      </span>

      {unread
        ? <span className="bg-primary mt-1.5 size-2 shrink-0 rounded-full" />
        : null}
    </Button>
  );
}

NotificationRow.displayName = 'NotificationRow';


/** Notification bell + dropdown center (#165, .pen Notif Panel). Lives in the
 * shared /app shell so it shows on every page. Polls every 30s and on window
 * focus; the query is gated on a selected seller. Item click marks the row read
 * and deep-links via its payload (part_id → inventory drawer). */
function NotificationCenter({ sellerId }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const feedQuery = useNotificationsQuery(sellerId, {
    select: selectFeed,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });
  const queryKey = feedQuery.queryKey;

  const markReadMutation = useNotificationMarkReadMutation();

  const data = feedQuery.data;
  const notifications = data?.results || [];
  const unread = data?.unread || 0;
  const badge = unread > 99 ? '99+' : String(unread);

  const markAllRead = () => {
    if (!unread) {
      return;
    }
    markReadMutation.mutate({ payload: { all: true, seller: sellerId }, queryKey });
  };

  const openNotification = (notification) => {
    if (!notification.read_at) {
      markReadMutation.mutate({ payload: { ids: [notification.id] }, queryKey });
    }
    setOpen(false);
    const partId = notification.payload?.part_id;
    if (partId) {
      navigate({ to: '/app/inventory', search: { part: partId } });
    }
    else if (notification.payload?.sale_id || notification.event === 'weekly_summary') {
      navigate({ to: '/app/sales' });
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          aria-label={unread ? `Notifications, ${badge} unread` : 'Notifications'}
          className={cn(
            'relative grid size-[38px] cursor-pointer place-items-center rounded-lg border border-hairline p-0',
            'bg-surface-2 text-ink-muted hover:bg-surface-2 hover:text-ink',
          )}
        >
          <Bell className="size-[18px]!" />

          {unread
            ? (
              <span
                className={cn(
                  'bg-primary absolute -right-1 -top-1 grid h-[17px] min-w-[17px] place-items-center',
                  'rounded-full px-1 text-[9.5px] font-bold leading-none text-white',
                )}
              >
                {badge}
              </span>
            )
            : null}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        aria-label="Notifications"
        className={cn(
          'z-50 flex max-h-[80vh] w-[calc(100vw-1.25rem)] flex-col rounded-2xl border border-hairline',
          'bg-surface-1 p-0 shadow-2xl lg:max-h-[560px] lg:w-[382px]',
        )}
        collisionPadding={8}
      >
        <div className="flex items-center justify-between border-b border-hairline px-4 py-3.5">
          <span className="text-[15px] font-semibold text-ink">Notifications</span>

          <Button
            variant="ghost"
            disabled={!unread || markReadMutation.isPending}
            className={cn(
              'h-auto rounded-none bg-transparent p-0 text-[12.5px] font-medium text-primary',
              'hover:bg-transparent hover:text-primary disabled:opacity-40',
            )}
            onClick={markAllRead}
          >
            Mark all read
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {feedQuery.isPending
            ? <span className="block px-4 py-10 text-center text-[13px] text-ink-subtle">Loading…</span>
            : feedQuery.isError
              ? (
                <span className="block px-4 py-10 text-center text-[13px] text-danger">
                  Could not load notifications.
                </span>
              )
              : notifications.length === 0
                ? (
                  <span className="block px-4 py-12 text-center text-[13px] text-ink-subtle">
                    You&apos;re all caught up.
                  </span>
                )
                : notifications.map((notification) => (
                  <NotificationRow
                    key={notification.id}
                    notification={notification}
                    onClick={() => openNotification(notification)}
                  />
                ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

NotificationCenter.displayName = 'NotificationCenter';
export default NotificationCenter;
